import React from "react";
import { MatchScore } from "@/hooks/use-match-score";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CheckCircle2, XCircle, Sparkles } from "lucide-react";

interface MatchScoreBadgeProps {
  match: MatchScore;
  /** compact = small chip for cards; full = larger pill for profile header */
  variant?: "compact" | "full";
}

const COLOR_MAP: Record<
  MatchScore["label"],
  { bg: string; text: string; ring: string; bar: string }
> = {
  Excellent: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    ring: "ring-emerald-200",
    bar: "bg-emerald-500",
  },
  Good: {
    bg: "bg-green-50",
    text: "text-green-700",
    ring: "ring-green-200",
    bar: "bg-green-500",
  },
  Fair: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    ring: "ring-amber-200",
    bar: "bg-amber-400",
  },
  Low: {
    bg: "bg-red-50",
    text: "text-red-600",
    ring: "ring-red-200",
    bar: "bg-red-400",
  },
};

export function MatchScoreBadge({
  match,
  variant = "compact",
}: MatchScoreBadgeProps) {
  const colors = COLOR_MAP[match.label];

  const badge = (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full ring-1 cursor-default select-none",
        colors.bg,
        colors.text,
        colors.ring,
        variant === "compact"
          ? "px-2 py-0.5 text-xs font-medium"
          : "px-3 py-1 text-sm font-semibold"
      )}
    >
      <Sparkles className={cn("shrink-0", variant === "compact" ? "h-3 w-3" : "h-4 w-4")} />
      <span>{match.score}% match</span>
    </div>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          className="max-w-[240px] p-3 space-y-2"
        >
          {/* Score bar */}
          <div className="flex items-center gap-2 mb-1">
            <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
              <div
                className={cn("h-full rounded-full transition-all", colors.bar)}
                style={{ width: `${match.score}%` }}
              />
            </div>
            <span className={cn("text-xs font-bold", colors.text)}>
              {match.score}%
            </span>
          </div>

          <p className="text-xs font-semibold text-foreground">
            {match.label} match for your preferences
          </p>

          {match.reasons.length > 0 && (
            <ul className="space-y-0.5">
              {match.reasons.map((r) => (
                <li key={r} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                  <CheckCircle2 className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" />
                  {r}
                </li>
              ))}
            </ul>
          )}

          {match.misses.length > 0 && (
            <ul className="space-y-0.5">
              {match.misses.map((m) => (
                <li key={m} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                  <XCircle className="h-3 w-3 text-red-400 mt-0.5 shrink-0" />
                  {m}
                </li>
              ))}
            </ul>
          )}

          <p className="text-[10px] text-muted-foreground/70 pt-1 border-t">
            Based on your care preferences · update in Settings
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
